import { Functions, httpsCallable } from "firebase/functions";

/**
 * Payload for the server-side `registerStudent` callable. Mirrors the server's
 * whitelist (see `functions/api/users/registerStudent.ts`) exactly so a contract
 * change surfaces here as a type error. The password is used only to create the
 * Firebase Auth user server-side and is never persisted.
 */
export interface RegisterStudentPayload {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  /**
   * Proof of the SMS code sent by `requestSignupPhoneVerificationCode`.
   * Required for real phone numbers unless the request callable answered
   * `not_required` (shared placeholder numbers or a pre-release cohort invite
   * link).
   */
  phoneVerification?: { code: string; requestId: string };
  state: string;
  city: string;
  password: string;
  dateOfBirth?: string;
  referralSource?: string;
  incarcerationStatus?: string;
  doesNotHaveEmail?: boolean;
  otherCity?: string;
  organizationId?: string;
  cohortId?: string;
  vocationId?: string;
  referredByPartnerId?: string;
  /** Referring student's uid (student referral; distinct from referredByPartnerId). */
  referredByUserId?: string;
  marketing?: {
    meta?: {
      fbc?: string;
      fbp?: string;
      fbclid?: string;
      capturedAt?: string;
    };
    google?: {
      gclid?: string;
      wbraid?: string;
      gbraid?: string;
      capturedAt?: string;
    };
    /** TikTok `ttclid` (URL) + `_ttp` cookie, captured at signup. */
    tiktok?: {
      ttclid?: string;
      ttp?: string;
      capturedAt?: string;
    };
    /** Standard UTM fields from the signup URL. */
    utm?: {
      source?: string;
      medium?: string;
      campaign?: string;
      term?: string;
      content?: string;
      capturedAt?: string;
    };
  };
}

interface RegisterStudentResponse {
  uid: string;
  /** Firebase custom token — the client signs in with it via signInWithCustomToken. */
  customToken: string;
}

/**
 * Calls the `registerStudent` backend callable, which creates the Firebase Auth
 * user + `users/{uid}` document and returns a custom token to sign in with.
 * Duplicate email/phone surface as a `functions/already-exists` error whose
 * `details.reason` is `"email"` or `"phone"`.
 */
export default async function registerStudent(
  payload: RegisterStudentPayload,
  functions: Functions
): Promise<RegisterStudentResponse> {
  const callable = httpsCallable<
    RegisterStudentPayload,
    RegisterStudentResponse
  >(functions, "registerStudent");
  const result = await callable(payload);
  return result.data;
}
